/**
 * LookupAdminRow — compact + expanded row in the lookup admin list
 * (Block 6.C.1).
 *
 * Compact (default): display_name + slug + active-pip + record_count.
 * Tap chevron → expand to surface rare actions (rename slug, deprecate /
 * restore, initiate merge).
 *
 * Editing semantics:
 *   - display_name → EditableField (auto-save-on-blur via useAutoSave;
 *     optimistic + undo per ADR-024 D-edit-mutation-strategy).
 *   - is_active → toggle button (optimistic + undo).
 *   - slug → per-form Save sub-form (LookupRenameSlugForm); ADR-024
 *     three-criterion principle.
 *
 * Merge: subcategories + specialties only (service-areas excluded per
 * ADR-020 §1). Initiate-merge calls back to the page-level state, which
 * surfaces LookupMergePreview with this row as source.
 *
 * ADR-017: F1 (no modal); F4 (always-visible affordances on expansion);
 * F7 (deprecate / restore is reversible — no confirm dialog; merge is
 * destructive but uses inline-confirmation pane, not a Dialog).
 */

import { useState, type ReactNode } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { LookupAdminRow, LookupTypeUrl, ServiceAreaAdminRow } from '@/lib/types';
import { lookupsApi } from '@/lib/lookups';
import { pageWriteText } from '@/lib/errorCopy';
import { useSupportContact } from '@/hooks/useSupportContact';
import { formatRecordCount } from '@/lib/format';
import { EditableField } from './EditableField';
import { LookupRenameSlugForm } from './LookupRenameSlugForm';
import { refreshFamily } from '@/lib/refreshFamily';

export interface LookupAdminRowProps {
  tenantSlug: string;
  type: LookupTypeUrl;
  row: LookupAdminRow | ServiceAreaAdminRow;
  /** Cache key for the admin-list query so optimistic + invalidation works cleanly. */
  invalidationKey: ReadonlyArray<unknown>;
  /**
   * Page-level callback when user taps "Initiate merge" on this row. The
   * parent flips to merge-source-pending state and renders the merge
   * preview pane above the list. Undefined for service-areas (no merge).
   */
  onInitiateMerge?: (row: LookupAdminRow) => void;
  /**
   * If non-null, this row is currently the merge target candidate. Used
   * to render a Pick / Cancel affordance for choosing target.
   */
  mergePickHandler?: () => void;
  /**
   * If non-null, this row is currently the merge source. Other rows in
   * the list show a "Pick as target" affordance instead of expand.
   */
  isMergeSource?: boolean;
}

/** One lookup row's patch, read at the press. */
interface LookupPatchVars {
  type: LookupTypeUrl;
  id: string;
  body: { display_name: string } | { is_active: boolean };
  family: readonly unknown[];
}

/** PATCH one lookup row by its type. */
function patchLookupRow(tenantSlug: string, v: LookupPatchVars) {
  if (v.type === 'subcategories') return lookupsApi.patchSubcategory(tenantSlug, v.id, v.body);
  if (v.type === 'specialties') return lookupsApi.patchSpecialty(tenantSlug, v.id, v.body);
  if (v.type === 'highlights') return lookupsApi.patchHighlight(tenantSlug, v.id, v.body);
  return lookupsApi.patchServiceArea(tenantSlug, v.id, v.body);
}

export function LookupAdminRow({
  tenantSlug,
  type,
  row,
  invalidationKey,
  onInitiateMerge,
  mergePickHandler,
  isMergeSource = false,
}: LookupAdminRowProps): ReactNode {
  const queryClient = useQueryClient();
  const contact = useSupportContact();
  const [expanded, setExpanded] = useState(false);
  const [renamingSlug, setRenamingSlug] = useState(false);

  // ---- PATCH display_name and is_active ----
  // The row, its type, the change and the list to refresh (the family)
  // are read at the press and travel as the variables.
  const editDisplayMutation = useMutation({
    mutationFn: async (v: LookupPatchVars) => patchLookupRow(tenantSlug, v),
    onSuccess: (_res, v) => {
      void queryClient.invalidateQueries({ queryKey: v.family });
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: async (v: LookupPatchVars) => patchLookupRow(tenantSlug, v),
    onSuccess: (_res, v) => {
      void queryClient.invalidateQueries({ queryKey: v.family });
    },
  });

  function handleEditDisplayName(newValue: string): Promise<void> {
    return editDisplayMutation
      .mutateAsync({ type, id: row.id, body: { display_name: newValue }, family: refreshFamily(invalidationKey) })
      .then(() => undefined);
  }

  function handleToggleActive(): void {
    toggleActiveMutation.mutate({ type, id: row.id, body: { is_active: !row.is_active }, family: refreshFamily(invalidationKey) });
  }

  // Show "Pick as target" if some other row is the merge source.
  if (mergePickHandler) {
    return (
      <div className="border border-sage-400/40 bg-sage-50 rounded-md p-3 mb-2">
        <div className="flex items-center justify-between gap-2">
          <div className="flex-1">
            <div className="text-sm font-semibold">{row.display_name}</div>
            <div className="text-xs text-ink-soft font-mono">{row.slug}</div>
            <div className="text-xs text-ink-soft mt-1">
              {formatRecordCount(row.record_count)} record{row.record_count === 1 ? '' : 's'}
            </div>
          </div>
          <button
            type="button"
            onClick={mergePickHandler}
            aria-label={`Pick "${row.display_name}" as merge target`}
            className="min-h-11 px-3 py-1 text-sm bg-sage-800 text-cream rounded-md hover:bg-forest-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
          >
            Pick as target
          </button>
        </div>
      </div>
    );
  }

  // Show "merge source" badge if THIS row is the source.
  if (isMergeSource) {
    return (
      <div className="border-2 border-amber-400 bg-amber-50 rounded-md p-3 mb-2">
        <div className="text-xs font-semibold text-amber-800 uppercase tracking-wide mb-1">
          Merging from
        </div>
        <div className="text-sm font-semibold">{row.display_name}</div>
        <div className="text-xs text-ink-soft font-mono">{row.slug}</div>
        <div className="text-xs text-ink-soft mt-1">
          {formatRecordCount(row.record_count)} record{row.record_count === 1 ? '' : 's'}
        </div>
      </div>
    );
  }

  // Phase 1.10 — Service Model rows render as read-only (no expand /
  // edit / merge affordances). Service Model admin CRUD ships in a
  // follow-up sub-block; the migration-seeded catalog covers the
  // empirical vocabulary for Era A so the read-only view is sufficient
  // for steward visibility today.
  if (type === 'service-model') {
    return (
      <div className={'border border-sage-800/[8%] rounded-md mb-2 px-3 py-2 ' + (row.is_active ? 'bg-paper' : 'bg-sand')}>
        <div className="text-sm font-semibold">{row.display_name}</div>
        <div className="text-xs text-ink-soft font-mono">{row.slug}</div>
        <div className="text-xs text-ink-soft mt-0.5">
          <span
            aria-label={row.is_active ? 'active' : 'hidden'}
            className={
              'inline-block w-2 h-2 rounded-pill mr-1 ' +
              (row.is_active ? 'bg-green-500' : 'bg-sage-300')
            }
          />
          {row.is_active ? 'active' : 'hidden'} · {formatRecordCount(row.record_count)} record
          {row.record_count === 1 ? '' : 's'}
        </div>
      </div>
    );
  }

  return (
    <div className={'border border-sage-800/[8%] rounded-md mb-2 ' + (row.is_active ? 'bg-paper' : 'bg-sand')}>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        aria-label={`${row.display_name} (tap to ${expanded ? 'collapse' : 'expand'})`}
        className="w-full text-left px-3 py-2 min-h-11 flex items-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
      >
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold truncate">{row.display_name}</div>
          <div className="text-xs text-ink-soft font-mono truncate">{row.slug}</div>
          <div className="text-xs text-ink-soft mt-0.5">
            <span
              aria-label={row.is_active ? 'active' : 'hidden'}
              className={
                'inline-block w-2 h-2 rounded-pill mr-1 ' +
                (row.is_active ? 'bg-green-500' : 'bg-sage-300')
              }
            />
            {row.is_active ? 'active' : 'hidden'} · {formatRecordCount(row.record_count)} record
            {row.record_count === 1 ? '' : 's'}
          </div>
        </div>
        {expanded ? (
          <ChevronUp size={18} aria-hidden="true" className="text-ink-soft shrink-0" />
        ) : (
          <ChevronDown size={18} aria-hidden="true" className="text-ink-soft shrink-0" />
        )}
      </button>

      {expanded && (
        <div className="border-t border-sage-800/[8%] px-3 py-3">
          <div className="text-xs text-ink-soft mb-1">Display name</div>
          <div className="mb-3">
            <EditableField
              value={row.display_name}
              onSave={handleEditDisplayName}
              label={`${row.display_name} display name`}
            />
          </div>

          <div className="text-xs text-ink-soft mb-1">Slug</div>
          <div className="mb-3 flex items-center gap-2">
            <span className="text-sm font-mono text-ink-soft flex-1">{row.slug}</span>
            <button
              type="button"
              onClick={() => setRenamingSlug(true)}
              className="min-h-11 px-2 py-1 text-xs text-amber-700 border border-amber-300 rounded-md hover:bg-amber-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
            >
              Rename slug →
            </button>
          </div>

          {renamingSlug && (
            <LookupRenameSlugForm
              tenantSlug={tenantSlug}
              type={type}
              rowId={row.id}
              currentSlug={row.slug}
              recordCount={row.record_count}
              invalidationKey={invalidationKey}
              onClose={() => setRenamingSlug(false)}
            />
          )}

          <div className="text-xs text-ink-soft mb-1 mt-3">Status</div>
          <div className="mb-3 flex items-center gap-2">
            <span className="text-sm flex-1">
              {row.is_active ? 'Active' : 'Hidden'}
            </span>
            <button
              type="button"
              onClick={handleToggleActive}
              disabled={toggleActiveMutation.isPending}
              aria-pressed={row.is_active}
              className={
                'min-h-11 px-3 py-1 text-sm border rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 ' +
                (row.is_active
                  ? 'border-sage-800/[8%] text-ink-soft hover:bg-sand'
                  : 'border-green-400 text-green-700 hover:bg-green-50')
              }
            >
              {toggleActiveMutation.isPending
                ? 'Saving…'
                : row.is_active
                  ? 'Hide'
                  : 'Restore'}
            </button>
          </div>

          {/* Initiate merge — subcategories + specialties only (ADR-020 §1) */}
          {onInitiateMerge && type !== 'service-areas' && (
            <div className="mt-3 pt-3 border-t border-sage-800/[8%]">
              <button
                type="button"
                onClick={() => onInitiateMerge(row as LookupAdminRow)}
                className="min-h-11 w-full px-3 py-1 text-sm text-amber-700 border border-amber-300 rounded-md hover:bg-amber-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
              >
                Initiate merge →
              </button>
              <div className="text-xs text-ink-soft mt-1">
                Re-points {formatRecordCount(row.record_count)} record
                {row.record_count === 1 ? '' : 's'} to a target row, then deletes this row.
                Audit-logged; not session-undoable.
              </div>
            </div>
          )}

          {editDisplayMutation.isError && (
            <div role="alert" className="text-xs text-red-700 mt-2" data-testid="lookup-row-edit-error">
              ⚠ {pageWriteText(editDisplayMutation.error, contact)}
            </div>
          )}
          {toggleActiveMutation.isError && (
            <div role="alert" className="text-xs text-red-700 mt-2" data-testid="lookup-row-toggle-error">
              ⚠ {pageWriteText(toggleActiveMutation.error, contact)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
